import React, { createContext, useContext, useState, useEffect } from 'react';
import { api, getToken, setToken, removeToken } from '../api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setTokenState] = useState(getToken());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadUser() {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await api.getMe();
        if (res.success && res.data?.user) {
          setUser(res.data.user);
        } else {
          removeToken();
          setTokenState(null);
          setUser(null);
        }
      } catch (err) {
        console.error('Failed to load user:', err);
        removeToken();
        setTokenState(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    }
    loadUser();
  }, [token]);

  const login = async (email, password) => {
    const res = await api.login(email, password);
    if (res.success && res.data?.token) {
      setToken(res.data.token);
      setTokenState(res.data.token);
      setUser(res.data.user);
      return res.data.user;
    }
    throw new Error(res.error?.message || 'Login failed');
  };

  const register = async (email, password, name) => {
    const res = await api.register(email, password, name);
    if (res.success && res.data?.token) {
      setToken(res.data.token);
      setTokenState(res.data.token);
      setUser(res.data.user);
      return res.data.user;
    }
    throw new Error(res.error?.message || 'Registration failed');
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch (e) {
      // ignore
    }
    removeToken();
    setTokenState(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
